const BASE = import.meta.env.VITE_API_URL || '/api';

let accessToken = null;
let refreshToken = null;

const ACCESS_KEY = 'np_access';
const REFRESH_KEY = 'np_refresh';

(function bootstrap() {
  try {
    accessToken = sessionStorage.getItem(ACCESS_KEY);
    refreshToken = localStorage.getItem(REFRESH_KEY);
  } catch {
    /* sessionStorage / localStorage may be unavailable */
  }
})();

export function setTokens({ accessToken: a, refreshToken: r } = {}) {
  accessToken = a || null;
  refreshToken = r || null;
  try {
    if (a) sessionStorage.setItem(ACCESS_KEY, a);
    else sessionStorage.removeItem(ACCESS_KEY);
    if (r) localStorage.setItem(REFRESH_KEY, r);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

export function getRefreshToken() {
  return refreshToken;
}

export function clearTokens() {
  setTokens({ accessToken: null, refreshToken: null });
}

let refreshing = null;
async function refreshAccess() {
  if (!refreshToken) return false;
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const data = await r.json();
        setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
        return true;
      })
      .catch(() => false)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export async function apiFetch(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(options.headers || {}),
  };

  let res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401 && refreshToken && !options._retried) {
    const ok = await refreshAccess();
    if (ok) {
      return apiFetch(path, { ...options, _retried: true });
    }
    clearTokens();
  }
  return res;
}

async function asJson(res) {
  const text = await res.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed: ${res.status}`);
    err.status = res.status;
    err.details = data && data.details;
    throw err;
  }
  return data;
}

// ---- Auth ----
export const Auth = {
  async register(payload) {
    const data = await asJson(await apiFetch('/auth/register', { method: 'POST', body: JSON.stringify(payload) }));
    setTokens(data);
    return data;
  },
  async login(email, password) {
    const data = await asJson(await apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }));
    setTokens(data);
    return data;
  },
  async logout() {
    try { await apiFetch('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }); } catch {}
    clearTokens();
  },
  async seedAdmin() {
    return asJson(await apiFetch('/auth/seed-admin', { method: 'POST', body: JSON.stringify({}) }));
  },
  async changePassword(payload) {
    return asJson(await apiFetch('/auth/change-password', { method: 'PUT', body: JSON.stringify(payload) }));
  },
  async deleteAccount() {
    return asJson(await apiFetch('/auth/me', { method: 'DELETE' }));
  },
  async forgotPassword(email) {
    return asJson(await apiFetch('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }));
  },
  async resetPassword(token, password) {
    return asJson(await apiFetch('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }));
  },
};

// ---- Nurses ----
export const Nurses = {
  me: async () => asJson(await apiFetch('/nurses/me')),
  updateMe: async (profile) => asJson(await apiFetch('/nurses/me', { method: 'PUT', body: JSON.stringify(profile) })),
  list: async (page = 1) => asJson(await apiFetch(`/nurses?page=${page}`)),
  get: async (id) => asJson(await apiFetch(`/nurses/${id}`)),
  update: async (id, patch) => asJson(await apiFetch(`/nurses/${id}`, { method: 'PUT', body: JSON.stringify(patch) })),
  toggleContractInterest: async (interested) => asJson(await apiFetch('/nurses/me/contract-interest', { method: 'PUT', body: JSON.stringify({ interested }) })),
  authorizeContract: async (nurseId, authorized) => asJson(await apiFetch(`/nurses/${nurseId}/authorize-contract`, { method: 'PUT', body: JSON.stringify({ authorized }) })),
  setAvailability: async (nurseId, open) => asJson(await apiFetch(`/nurses/${nurseId}/availability`, { method: 'PUT', body: JSON.stringify({ contractInterestOpen: open }) })),
  sendEmail: async (nurseId, payload) => asJson(await apiFetch(`/nurses/${nurseId}/send-email`, { method: 'POST', body: JSON.stringify(payload) })),
  changeRole: async (nurseId, role) => asJson(await apiFetch(`/nurses/${nurseId}/role`, { method: 'PUT', body: JSON.stringify({ role }) })),
  findByEmail: async (email) => asJson(await apiFetch(`/nurses/lookup?email=${encodeURIComponent(email)}`)),
};

// ---- Contracts ----
export const Contracts = {
  list: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return asJson(await apiFetch(`/contracts${q ? `?${q}` : ''}`));
  },
  create: async (contract) => asJson(await apiFetch('/contracts', { method: 'POST', body: JSON.stringify(contract) })),
  update: async (id, contract) => asJson(await apiFetch(`/contracts/${id}`, { method: 'PUT', body: JSON.stringify(contract) })),
  remove: async (id) => asJson(await apiFetch(`/contracts/${id}`, { method: 'DELETE' })),
  recordView: (id) => apiFetch(`/contracts/${id}/view`, { method: 'POST', body: JSON.stringify({}) }),
  views: async (id) => asJson(await apiFetch(`/contracts/${id}/views`)),
};

// ---- Interests ----
export const Interests = {
  mine: async () => asJson(await apiFetch('/interests/mine')),
  create: async (contractId) => asJson(await apiFetch('/interests', { method: 'POST', body: JSON.stringify({ contractId }) })),
  remove: async (contractId) => asJson(await apiFetch(`/interests/${contractId}`, { method: 'DELETE' })),
  byContract: async () => asJson(await apiFetch('/interests/by-contract')),
  byNurse: async (nurseId) => asJson(await apiFetch(`/interests/by-nurse/${nurseId}`)),
  adminRemove: async (nurseId, contractId) => asJson(await apiFetch(`/interests/admin`, { method: 'DELETE', body: JSON.stringify({ nurseId, contractId }) })),
};

// ---- Documents ----
export const Documents = {
  list: async (nurseId) => asJson(await apiFetch(`/documents${nurseId ? `?nurseId=${nurseId}` : ''}`)),
  upload: async (file, categoryKey) => {
    const fd = new FormData();
    fd.append('file', file);
    if (categoryKey) { fd.append('categoryKey', categoryKey); fd.append('category', categoryKey); }
    return asJson(await apiFetch('/documents/upload', { method: 'POST', body: fd }));
  },
  remove: async (id) => asJson(await apiFetch(`/documents/${id}`, { method: 'DELETE' })),
  downloadUrl: async (id) => asJson(await apiFetch(`/documents/${id}/download`)),
};

// ---- Categories ----
export const Categories = {
  list: async () => asJson(await apiFetch('/categories')),
  update: async (key, patch) => asJson(await apiFetch(`/categories/${key}`, { method: 'PUT', body: JSON.stringify(patch) })),
};

// ---- Schedules ----
export const Schedules = {
  list: async () => asJson(await apiFetch('/schedules')),
  listForNurse: async (nurseId) => asJson(await apiFetch(`/schedules?nurseId=${nurseId}`)),
  upload: async (file, title, periodLabel, nurseId) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('periodLabel', periodLabel);
    if (nurseId) fd.append('nurseId', nurseId);
    return asJson(await apiFetch('/schedules', { method: 'POST', body: fd }));
  },
  downloadUrl: async (id) => asJson(await apiFetch(`/schedules/${id}/download`)),
  remove: async (id) => asJson(await apiFetch(`/schedules/${id}`, { method: 'DELETE' })),
};

// ---- Messages ----
export const Messages = {
  threads: async () => asJson(await apiFetch('/messages/threads')),
  list: async (threadId) => asJson(await apiFetch(`/messages/${threadId}`)),
  send: async (threadId, body) => asJson(await apiFetch(`/messages/${threadId}`, { method: 'POST', body: JSON.stringify({ body }) })),
  newThread: async (nurseId) => asJson(await apiFetch('/messages/new-thread', { method: 'POST', body: JSON.stringify({ nurseId }) })),
};

// ---- Audit ----
export const Audit = {
  logs: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return asJson(await apiFetch(`/audit/logs${q ? `?${q}` : ''}`));
  },
  access: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return asJson(await apiFetch(`/audit/access${q ? `?${q}` : ''}`));
  },
};

// ---- Admin Permissions ----
export const AdminPermissions = {
  list: async () => asJson(await apiFetch('/admin-permissions')),
  update: async (adminId, perms) =>
    asJson(await apiFetch(`/admin-permissions/${adminId}`, { method: 'PUT', body: JSON.stringify(perms) })),
};

// ---- Access Codes ----
export const AccessCodes = {
  list: async () => asJson(await apiFetch('/access-codes')),
  create: async () => asJson(await apiFetch('/access-codes', { method: 'POST', body: JSON.stringify({}) })),
  revoke: async (id) => asJson(await apiFetch(`/access-codes/${id}`, { method: 'DELETE' })),
  validate: async (code) => asJson(await apiFetch('/access-codes/validate', { method: 'POST', body: JSON.stringify({ code }) })),
};

// ---- Notifications ----
export const Notifications = {
  mySettings: async () => asJson(await apiFetch('/notifications/settings')),
  updateMySettings: async (patch) => asJson(await apiFetch('/notifications/settings', { method: 'PUT', body: JSON.stringify(patch) })),
  allSettings: async () => asJson(await apiFetch('/notifications/settings')),
  updateNurseSettings: async (nurseId, patch) => asJson(await apiFetch(`/notifications/settings/${nurseId}`, { method: 'PUT', body: JSON.stringify(patch) })),
  sendMassEmail: async (payload) => asJson(await apiFetch('/notifications/mass-email', { method: 'POST', body: JSON.stringify(payload) })),
};
